import { useTranslation } from 'react-i18next'
import { objectArrayKey } from './i18n-array'
import type { MarketingPageKey } from './links'

interface FaqSectionProps {
  readonly contentKey: MarketingPageKey
}

interface FaqItem {
  readonly question: string
  readonly answer: string
}

/**
 * Visible FAQ list rendered from `marketing.<contentKey>.faq`, the same source
 * as the FAQPage JSON-LD — so the structured data always matches what users
 * see (Google's requirement). Renders nothing when the page has no FAQ.
 */
export function FaqSection({ contentKey }: FaqSectionProps) {
  const { t } = useTranslation()
  const items = objectArrayKey<FaqItem>(t, `marketing.${contentKey}.faq`)
  if (items.length === 0) return null

  return (
    <section className="mx-auto max-w-3xl px-6 py-10">
      <h2 className="mb-6 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {t('marketing.common.faqHeading')}
      </h2>
      <dl className="space-y-6">
        {items.map((item) => (
          <div key={item.question}>
            <dt className="text-lg font-semibold text-foreground">{item.question}</dt>
            <dd className="mt-2 leading-relaxed text-muted-foreground">{item.answer}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
